"use client";
import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Icon from "@/components/primitives/icon";
import Button from "@/components/primitives/button";
// @ts-ignore
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import "@splidejs/react-splide/css/core";

interface BigWinner {
  id: number;
  image_url: string;
  name?: string;
}

interface BigWinnersLightboxProps {
  bigWinners: BigWinner[];
}

export default function BigWinnersLightbox({
  bigWinners,
}: BigWinnersLightboxProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % bigWinners.length);
  };

  const prevImage = () => {
    setSelectedImageIndex(
      (prev) => (prev - 1 + bigWinners.length) % bigWinners.length
    );
  };

  return (
    <>
      <div className="relative mx-auto max-w-[798px]">
        {bigWinners.length >= 3 ? (
          // Use slider for 3 or more items
          <Splide
            hasTrack={false}
            options={{
              type: "slide",
              perPage: 3,
              gap: "1rem",
              arrows: true,
              pagination: false,
            }}
          >
            <SplideTrack>
              {bigWinners.map((winner, index) => (
                <SplideSlide key={winner.id}>
                  <div className="p-2">
                    <div
                      className="group relative rounded-md overflow-hidden text-center cursor-pointer"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={winner.image_url}
                        alt={winner.name || `Big Winner ${winner.id}`}
                        className="rounded-md group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Icon
                            name="search-alternate"
                            color="white"
                            size="lg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </SplideSlide>
              ))}
            </SplideTrack>
            <div className="flex justify-center items-center gap-4 mt-12 splide__arrows transform">
              <button className="flex justify-center items-center bg-secondary-700 rounded-full w-10 h-10 text-white splide__arrow splide__arrow--prev">
                <Icon name="arrow-left" color="dark" size="sm" />
              </button>
              <button className="flex justify-center items-center bg-secondary-700 rounded-full w-10 h-10 text-white splide__arrow splide__arrow--next">
                <Icon name="arrow-right" color="dark" size="sm" />
              </button>
            </div>
          </Splide>
        ) : (
          // Use centered layout for fewer than 3 items
          <div className="flex justify-center items-center gap-4">
            {bigWinners.map((winner, index) => (
              <div key={winner.id} className="p-2">
                <div
                  className="group relative rounded-md overflow-hidden text-center cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={winner.image_url}
                    alt={winner.name || `Big Winner ${winner.id}`}
                    className="rounded-md max-w-[240px] group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Icon name="search-alternate" color="white" size="lg" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog.Root open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="z-50 fixed inset-0 bg-black bg-opacity-90 data-[state=closed]:animate-overlayHide data-[state=open]:animate-overlayShow" />
          <Dialog.Content className="z-50 fixed inset-0 flex justify-center items-center p-4">
            <div className="relative flex justify-center items-center max-w-[90vw] max-h-[90vh]">
              <Dialog.Close asChild>
                <Button
                  variant="circle"
                  color="tertiary"
                  className="top-4 right-4 z-10 absolute bg-black bg-opacity-50 hover:bg-opacity-75"
                  onClick={closeLightbox}
                >
                  <Icon name="close" size="sm" color="dark" />
                </Button>
              </Dialog.Close>

              {bigWinners.length > 1 && (
                <Button
                  variant="circle"
                  color="tertiary"
                  className="top-1/2 left-4 z-10 absolute bg-black bg-opacity-50 hover:bg-opacity-75 -translate-y-1/2 transform"
                  onClick={prevImage}
                >
                  <Icon name="arrow-left" size="sm" color="dark" />
                </Button>
              )}

              <img
                src={bigWinners[selectedImageIndex]?.image_url}
                alt={
                  bigWinners[selectedImageIndex]?.name ||
                  `Big Winner ${bigWinners[selectedImageIndex]?.id}`
                }
                className="rounded-lg max-w-full max-h-full object-contain"
              />
              {bigWinners.length > 1 && (
                <Button
                  variant="circle"
                  color="tertiary"
                  className="top-1/2 right-4 z-10 absolute bg-black bg-opacity-50 hover:bg-opacity-75 -translate-y-1/2 transform"
                  onClick={nextImage}
                >
                  <Icon name="arrow-right" size="sm" color="dark" />
                </Button>
              )}

              {bigWinners.length > 1 && (
                <div className="bottom-4 left-1/2 absolute bg-black bg-opacity-50 px-3 py-1 rounded-full text-white text-sm -translate-x-1/2 transform">
                  {selectedImageIndex + 1} / {bigWinners.length}
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
